// src/components/frames/TicketsStoreProvider.js
import Vue from 'vue'
import TicketsRealtimeService from '@/services/RealTime/TicketsRealtimeService'
import { makeRealtimeLogger } from '@/services/RealTime/realtimeCommon'
import TicketsService from '@/services/API/TicketsService'

const store = Vue.observable({
  byId: {},
  lastSeenTsById: {},
  started: false,
  realtimeReady: false,
  loading: false,
  loaded: false,
  initialLoadKey: null,
  additionalLoading: false
})

let lifecycleOwnerId = null
let nextInstanceId = 1

function toTs (value) {
  if (value === null || value === undefined) {
    return 0
  }

  if (typeof value === 'number') {
    return value
  }

  try {
    const d = new Date(value)
    const ts = d.getTime()
    if (Number.isNaN(ts)) {
      return 0
    }
    return ts
  } catch {
    return 0
  }
}

function getTicketId (ticket) {
  if (!ticket) {
    return null
  }

  return ticket._id || ticket.id || ticket.ticketId || ticket.referenceId || null
}

function getTicketUpdatedTs (ticket) {
  if (!ticket) {
    return 0
  }

  return (
    toTs(ticket.last_mod_dt) ||
    toTs(ticket.lastModDt) ||
    toTs(ticket.updatedAt) ||
    toTs(ticket.resolve_date) ||
    toTs(ticket.open_date) ||
    0
  )
}

function shouldApplyIncoming (incoming, current) {
  if (!current) {
    return true
  }

  const inTs = getTicketUpdatedTs(incoming)
  const curTs = getTicketUpdatedTs(current)

  if (!inTs || !curTs) {
    return true
  }

  return inTs >= curTs
}

function syncReactiveTicket (current, incoming) {
  if (!current || !incoming) {
    return incoming
  }

  for (const key of Object.keys(current)) {
    if (!(key in incoming)) {
      Vue.delete(current, key)
    }
  }

  for (const [key, value] of Object.entries(incoming)) {
    Vue.set(current, key, value)
  }

  return current
}

const TicketsStoreProvider = {
  name: 'TicketsStoreProvider',
  props: {
    hubStarted: {
      type: Boolean,
      default: null
    },

    scopes: {
      type: Array,
      default: () => []
    },

    manageLifecycle: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    _instanceId: nextInstanceId++,
    _logger: null,
    _realtimeInited: false,
    _initialLoadInFlight: false,
    _initialLoadKey: null,
    _initialLoadRetryTimer: null,
    _initialLoadRetryCount: 0,
    _additionalLoadInFlightByKey: {}
  }),
  computed: {
    allTickets () {
      return Object.values(store.byId)
    },

    activeTickets () {
      return Object.values(store.byId).filter((t) => {
        if (!t) {
          return false
        }
        return t.active === true
      })
    },

    runKey () {
      if (this.manageLifecycle !== true) {
        return null
      }

      if (this.hubStarted !== true) {
        return null
      }

      if (!this.scopes || !this.scopes.length) {
        return null
      }

      return this._getScopesKey()
    },

    storeIsLoading () {
      return store.loading
    },

    count () {
      return this.allTickets.length
    }
  },
  methods: {
    _isLifecycleOwner () {
      return this.manageLifecycle === true && lifecycleOwnerId === this._instanceId
    },

    _log (...args) {
      if (!this._logger) {
        this._logger = makeRealtimeLogger('[Tickets][StoreProvider]', (this.$root && this.$root.$log) || null)
      }

      if (this._logger) {
        this._logger(...args)
      }
    },

    _clearInitialLoadRetry () {
      if (this._initialLoadRetryTimer) {
        clearTimeout(this._initialLoadRetryTimer)
        this._initialLoadRetryTimer = null
      }
    },

    _scheduleInitialLoadRetry (reason, loadKey, error) {
      if (!this._isLifecycleOwner()) {
        return
      }

      if (!loadKey || this.runKey !== loadKey) {
        return
      }

      if (this._initialLoadRetryTimer) {
        return
      }

      const delays = [1000, 2000, 5000]
      const index = Math.min(this._initialLoadRetryCount, delays.length - 1)
      const delay = delays[index]

      this._initialLoadRetryCount += 1

      this._log('schedule initial load retry', {
        reason: reason || 'unknown',
        loadKey,
        retryCount: this._initialLoadRetryCount,
        delay,
        error: error ? (error.message || String(error)) : null
      })

      this.$emit('load-error', {
        reason: reason || 'unknown',
        retryCount: this._initialLoadRetryCount,
        delay,
        error: error || null
      })

      this._initialLoadRetryTimer = setTimeout(() => {
        this._initialLoadRetryTimer = null

        if (!this._isLifecycleOwner()) {
          return
        }

        if (this.runKey !== loadKey) {
          return
        }

        this._ensureInitialLoad('retry')
      }, delay)
    },

    _clearStore () {
      store.byId = {}
      store.lastSeenTsById = {}
    },

    upsert (ticket) {
      // this._log('upsert', { ticket })
      const nextTicket = ticket || null
      const id = getTicketId(nextTicket)
      if (!id) {
        return
      }

      const current = store.byId[id] || null

      const shouldApply = shouldApplyIncoming(nextTicket, current)
      if (!shouldApply) {
        return
      }

      if (current) {
        syncReactiveTicket(current, nextTicket)
      } else {
        Vue.set(store.byId, id, nextTicket)
      }

      const ts = getTicketUpdatedTs(nextTicket)
      if (ts) {
        Vue.set(store.lastSeenTsById, id, ts)
      }
    },

    remove (ticketId, meta) {
      this._log('remove', { ticketId, meta })
      if (!ticketId) {
        return
      }

      if (store.byId[ticketId]) {
        Vue.delete(store.byId, ticketId)
      }

      if (store.lastSeenTsById[ticketId]) {
        Vue.delete(store.lastSeenTsById, ticketId)
      }

      if (meta && meta.reason) {
        this._log('remove', { ticketId, reason: meta.reason })
      }
    },

    getTicket (ticketId) {
      if (!ticketId) {
        return null
      }

      return store.byId[ticketId] || null
    },

    listAll () {
      return this.allTickets
    },

    listActive () {
      return this.activeTickets
    },

    _initRealtimeIfNeeded () {
      this._log('_initRealtimeIfNeeded', { _realtimeInited: this._realtimeInited })

      if (!this._isLifecycleOwner()) {
        return
      }

      if (this._realtimeInited) {
        return
      }

      this._realtimeInited = true

      TicketsRealtimeService.init({
        fetchTicket: async (ticketId) => {
          return await TicketsService.getTicket(ticketId)
        },
        onUpsert: (ticket) => {
          this.upsert(ticket, { source: 'realtime_fetch' })
        },
        onRemove: (ticketId, data) => {
          // Scoped users may remove; full-access users should not send REMOVE.
          this.remove(ticketId, { reason: (data && data.reason) || 'REALTIME_REMOVE' })
        },
        onNeedsReload: (info) => {
          this._log('needs reload', info)
          this.$emit('needs-reload', info)
        },
        log: (...args) => {
          this._log(...args)
        }
      })

      store.realtimeReady = true
    },

    _startRealtime () {
      this._log('_startRealtime', {
        hubStarted: this.hubStarted,
        scopes: Array.isArray(this.scopes) ? this.scopes.length : 0,
        manageLifecycle: this.manageLifecycle
      })

      if (!this._isLifecycleOwner()) {
        return
      }

      if (this.hubStarted !== true) {
        return
      }

      if (!this.scopes || !this.scopes.length) {
        this._log('realtime not started (no scopes)')
        return
      }

      if (!store.loaded || store.initialLoadKey !== this.runKey) {
        this._log('realtime not started (tickets not loaded)')
        return
      }

      try {
        TicketsRealtimeService.start(this.scopes)
        store.started = true
        this._log('realtime started', { scopes: this.scopes.length })
      } catch (e) {
        this._log('realtime start failed', e)
      }
    },

    _stopRealtime (reason) {
      try {
        TicketsRealtimeService.stop()
      } catch {
        // ignore
      }

      store.started = false
      this._log('realtime stopped', { reason: reason || 'unknown' })
    },

    _getScopesKey () {
      const list = Array.isArray(this.scopes) ? this.scopes.slice() : []
      list.sort()
      return list.join('|')
    },
    _getScopesKeyFromList (scopes) {
      const list = Array.isArray(scopes) ? scopes.slice() : []
      list.sort()
      return list.join('|')
    },
    notifyLoading (value) {
      this._log('notifyLoading: ', value)
      this.$emit('store-loading', value)
    },
    async loadAdditional (scopes) {
      this._log('loadAdditional called', {
        instanceId: this._instanceId,
        isLifecycleOwner: this._isLifecycleOwner(),
        scopes,
        currentInFlightKeys: Object.keys(this._additionalLoadInFlightByKey || {})
      })

      const list = Array.isArray(scopes) ? scopes.filter(Boolean) : []

      if (!list.length) {
        this._log('loadAdditional skipped (no scopes)', {
          scopes
        })
        return
      }

      const loadKey = this._getScopesKeyFromList(list)

      this._log('loadAdditional normalized', {
        list,
        loadKey,
        hasInFlightMap: !!this._additionalLoadInFlightByKey
      })

      if (!loadKey) {
        this._log('loadAdditional skipped (empty loadKey)', {
          list
        })
        return
      }

      if (!this._additionalLoadInFlightByKey) {
        this._log('loadAdditional missing in-flight map, recreating')
        this._additionalLoadInFlightByKey = {}
      }

      if (this._additionalLoadInFlightByKey[loadKey] === true) {
        this._log('loadAdditional skipped (already in flight)', {
          loadKey
        })
        return
      }

      Vue.set(this._additionalLoadInFlightByKey, loadKey, true)
      store.additionalLoading = true

      this._log('additional load start', {
        scopes: list,
        loadKey,
        currentInFlightKeys: Object.keys(this._additionalLoadInFlightByKey)
      })

      try {
        await TicketsService.getTickets(
          list,
          {},
          {
            collect: false,
            onItem: (ticket) => {
              this.upsert(ticket, { source: 'additional_stream' })
            },
            onDone: (info) => {
              this._log('additional load done', {
                scopes: list,
                loadKey,
                info: info || null
              })
            }
          }
        )
      } catch (e) {
        this._log('additional load failed', {
          scopes: list,
          loadKey,
          error: e
        })
        throw e
      } finally {
        Vue.delete(this._additionalLoadInFlightByKey, loadKey)

        if (Object.keys(this._additionalLoadInFlightByKey).length === 0) {
          store.additionalLoading = false
        }

        this._log('additional load finalized', {
          loadKey,
          currentInFlightKeys: Object.keys(this._additionalLoadInFlightByKey),
          additionalLoading: store.additionalLoading
        })
      }
    },
    async _ensureInitialLoad (reason) {
      const key = this.runKey

      if (!key) {
        return
      }

      if (!this._isLifecycleOwner()) {
        return
      }

      if (this._initialLoadInFlight) {
        return
      }

      if (store.loaded && store.initialLoadKey === key) {
        return
      }

      const loadKey = key
      this._clearInitialLoadRetry()

      this._initialLoadInFlight = true
      this._initialLoadKey = key

      store.loading = true
      store.loaded = false
      store.initialLoadKey = key

      this._log('initial load start', { reason: reason || 'unknown', scopes: this.scopes.length })

      try {
        this.notifyLoading(true)
        await TicketsService.getTickets(
          this.scopes,
          {},
          {
            collect: false,
            onItem: (ticket) => {
              if (!this._isLifecycleOwner()) {
                return
              }

              if (this.runKey !== loadKey) {
                return
              }

              if (store.initialLoadKey !== loadKey || this._initialLoadKey !== loadKey) {
                return
              }

              // this._log('onItem', { loading: store.loading })
              this.upsert(ticket, { source: 'initial_stream' })
            },
            onDone: (info) => {
              if (!this._isLifecycleOwner()) {
                return
              }

              const rk = this.runKey
              if (!rk) {
                return
              }

              if (store.initialLoadKey !== rk || this._initialLoadKey !== rk) {
                return
              }

              if (store.loaded === true) {
                return
              }

              store.loaded = true
              store.loading = false
              this._initialLoadRetryCount = 0
              this.notifyLoading(false)
              this._log('initial load done', { scopes: this.scopes, info: info || null })
              this._startRealtime()
            }
          }
        )
      } catch (e) {
        if (store.initialLoadKey === loadKey && this._initialLoadKey === loadKey) {
          store.loading = false
          store.loaded = false
          this.notifyLoading(false)
        }

        this._log('initial load failed', e)
        this._scheduleInitialLoadRetry(reason || 'initial_load_failed', loadKey, e)
      } finally {
        this._log('initial finally')
        this._initialLoadInFlight = false
      }
    }
  },
  watch: {
    runKey: {
      handler (newKey, oldKey) {
        this._log('runKey watch', { newKey, oldKey })

        // Disabled -> Enabled
        if (newKey && !oldKey) {
          this._initRealtimeIfNeeded()
          this._ensureInitialLoad('run_enabled')
          return
        }

        // Enabled -> Disabled
        if (!newKey && oldKey) {
          this._clearInitialLoadRetry()

          if (store.started) {
            this._stopRealtime('run_disabled')
          }
          return
        }

        // Enabled -> Enabled (scope key changed)
        if (newKey && oldKey && newKey !== oldKey) {
          this._clearInitialLoadRetry()

          if (store.started) {
            this._stopRealtime('scopes_changed')
          }

          store.loaded = false
          store.loading = false
          store.initialLoadKey = null
          this._initialLoadKey = null
          this._initialLoadInFlight = false
          this._initialLoadRetryCount = 0
          this.notifyLoading(false)

          this._ensureInitialLoad('scopes_changed')
        }
      },
      immediate: true
    },
    storeIsLoading: {
      handler (value) {
        this._log('[notifyLoading] watch storeIsLoading: ', value)
        this.$emit('store-loading', value)
      },
      immediate: true
    }
  },
  created () {
    if (this.manageLifecycle === true) {
      if (lifecycleOwnerId === null) {
        lifecycleOwnerId = this._instanceId
      } else if (lifecycleOwnerId !== this._instanceId) {
        this._log('lifecycle owner already set', {
          ownerId: lifecycleOwnerId,
          instanceId: this._instanceId
        })
      }
    }

    this._log('created', {
      manageLifecycle: this.manageLifecycle,
      instanceId: this._instanceId,
      isLifecycleOwner: this._isLifecycleOwner()
    })
  },
  mounted () {
    this._log('mounted', {
      manageLifecycle: this.manageLifecycle,
      instanceId: this._instanceId,
      isLifecycleOwner: this._isLifecycleOwner()
    })
  },
  beforeDestroy () {
    this._log('beforeDestroy', {
      manageLifecycle: this.manageLifecycle,
      instanceId: this._instanceId,
      isLifecycleOwner: this._isLifecycleOwner()
    })

    this._clearInitialLoadRetry()

    if (this._isLifecycleOwner()) {
      this._stopRealtime('destroy')
      lifecycleOwnerId = null
    }
  },
  render () {
    if (this.$scopedSlots.default !== undefined) {
      return this.$scopedSlots.default({
        state: {
          ...store,
          allTickets: this.allTickets,
          activeTickets: this.activeTickets,
          count: this.count
        },
        methods: {
          upsert: this.upsert,
          remove: this.remove,
          getTicket: this.getTicket,
          listAll: this.listAll,
          listActive: this.listActive,
          stopRealtime: this._stopRealtime,
          loadAdditional: (...args) => this.loadAdditional(...args)
        }
      })
    }

    return this.$slots.default
  }
}

export default TicketsStoreProvider
